// The run page of a network run (spec 11 6.15.11, 6.5): the header, the plan review of a network plan (components,
// freshness, impact, outage changes first, warnings, Before you apply) and the live run of its components.
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, useState } from 'react';
import { Info, Play, RefreshCw, ShieldCheck, TriangleAlert } from 'lucide-react';
import { Link } from 'react-router';
import { PageContainer } from '../../app/AppLayout';
import { queryKeys, useNetwork } from '../../api/queries';
import type { ChangeAction, Run } from '../../api/types';
import { isTerminalRunStatus } from '../../api/types';
import { actionPresentation, runStatusPresentation } from '../../components/presentation';
import { ActionButton, StatusBadge } from '../../components/ui';
import { durationMs, formatClock, formatDateTime, formatDuration } from '../../lib/format';
import { childrenSummary, shortHash } from '../../lib/planReview';
import { CancelRunButton } from '../../runs/CancelRun';
import { LiveRun } from '../../runs/LiveRun';
import { RunIssues } from '../../runs/RunIssues';
import { RUN_KIND_ICON } from '../../runs/RunSummaryTable';
import { useRunEventLog } from '../../runs/useRunEventLog';
import { strings } from '../../strings';
import { ApplyNetworkDialog } from '../ApplyNetworkDialog';
import { WaitingText } from '../components';
import { isCurrentPlan, useAllAttachments, useStartNetworkRun } from '../page/hooks';
import { totalAttachments } from '../page/topology';
import { displayActor, grantPendingPresentation, isGrantPending, networkRunLabel } from '../shared';
import {
  componentLabel,
  componentOrder,
  componentTypeLabel,
  changesEndpoint,
  isOutage,
  namesFrom,
  outageSentence,
  retryWords,
} from './runHelpers';

const r = strings.networks.runs;
const rp = strings.runPage;
const p = strings.planReview;
const ACTIONS: readonly ChangeAction[] = ['create', 'update', 'replace', 'delete', 'no-change'];
const GLYPH: Record<ChangeAction, string> = {
  create: '+',
  update: '~',
  replace: '±',
  delete: '−',
  'no-change': '=',
};
const PLAN_WARNINGS = new Set(['DEPLOYMENT_APPLY_NEEDED', 'DNS_RECORD_CONFLICT', 'DNS_LINKS_NEAR_LIMIT']);

/** A network run's page (`/runs/:id` when the run's target is a network). */
export function NetworkRunPage({ run }: { run: Run }) {
  const queryClient = useQueryClient();
  const networkId = run.networkId ?? run.target?.id ?? '';
  const network = useNetwork(networkId, { enabled: !!networkId });
  const n = network.data?.data;
  const attachments = useAllAttachments(networkId);
  const names = namesFrom(attachments.data);
  const { log, stream } = useRunEventLog(run.id, run, (completed: Run) => {
    queryClient.setQueryData(queryKeys.run(run.id), completed);
    void queryClient.invalidateQueries({ queryKey: ['network', networkId] });
    void queryClient.invalidateQueries({ queryKey: ['networks'] });
    void queryClient.invalidateQueries({ queryKey: ['runs'] });
  });
  const { start, busy } = useStartNetworkRun({ id: networkId, name: n?.name ?? '' });
  const [applying, setApplying] = useState(false);
  const terminal = isTerminalRunStatus(run.status);
  const status = !terminal && log.runStatus ? log.runStatus : run.status;
  const networkName = n?.name ?? strings.networks.nav.kind;
  const deploymentName = names.get(run.attachmentDeploymentId ?? '') ?? null;
  const label = networkRunLabel(run, deploymentName);
  const KindIcon = RUN_KIND_ICON[run.kind] ?? Play;
  const startedAt =
    run.startedAt ??
    (status !== 'pending' ? (log.events.find((e) => e.kind === 'run-status')?.timestamp ?? null) : null);
  const review =
    run.kind === 'plan' && (run.status === 'completed' || (run.status === 'failed' && !!run.result?.plan));
  const fullScope = (run.scope ?? 'full') === 'full';
  const current = n ? isCurrentPlan(run, n) : true;
  const grant = isGrantPending({ ...run, status });

  return (
    <PageContainer full>
      <header className="run-header" data-testid="network-run-header">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <KindIcon aria-hidden className="size-5" />
            <h1 className="text-xl font-semibold">{r.title(label, networkName)}</h1>
            <span data-testid="run-status">
              <StatusBadge
                presentation={
                  grant
                    ? grantPendingPresentation(strings.networks.waiting.grantPendingFailed(retryWords(null)))
                    : runStatusPresentation[status]
                }
              />
            </span>
            <span className="badge tone-neutral">{fullScope ? rp.scopeFull : r.scopeAttachment}</span>
            {stream === 'live' && !terminal && (
              <span className="badge tone-success">
                <span aria-hidden className="status-dot bg-success" />
                {rp.live}
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-fg-muted" data-testid="network-run-context">
            {r.network}{' '}
            <Link className="link underline" to={`/networks/${networkId}`}>
              {networkName}
            </Link>
            {' · '}
            {startedAt ? (
              <span title={formatDateTime(startedAt)}>
                {rp.started(formatClock(startedAt), displayActor(run.createdBy))}
              </span>
            ) : (
              <>
                {rp.queued}
                {run.waitingFor && (
                  <>
                    {' · '}
                    <WaitingText waitingFor={run.waitingFor} />
                  </>
                )}
              </>
            )}
            {startedAt && terminal && (
              <>
                {' · '}
                {formatDuration(durationMs(startedAt, run.finishedAt))}
              </>
            )}
            {run.attachmentDeploymentId && (
              <>
                {' · '}
                {r.forDeployment}{' '}
                <Link className="link underline" to={`/deployments/${run.attachmentDeploymentId}`}>
                  {deploymentName ?? strings.networks.runLabels.aDeployment}
                </Link>
              </>
            )}
            {run.parentRunId && (
              <>
                {' · '}
                {r.queuedAfter}{' '}
                <Link className="link underline" to={`/runs/${run.parentRunId}`}>
                  {r.parentRun}
                </Link>
              </>
            )}
          </p>
        </div>
        <div className="run-actions">
          {review && fullScope && (
            <>
              <ActionButton
                permission="manage-networks"
                disabled={busy}
                icon={<RefreshCw aria-hidden className="size-4" />}
                onClick={() => void start({ kind: 'plan' })}
              >
                {p.planAgain}
              </ActionButton>
              <ActionButton
                permission="manage-networks"
                variant="primary"
                disabledReason={!n || n.activeRun ? strings.networks.page.runActive : null}
                icon={<Play aria-hidden className="size-4" />}
                onClick={() => setApplying(true)}
              >
                {p.applyThisPlan}
              </ActionButton>
            </>
          )}
          {!terminal && (status === 'pending' || status === 'running') && <CancelRunButton run={run} />}
        </div>
      </header>

      {review ? (
        <NetworkPlanReview
          run={run}
          names={names}
          networkName={networkName}
          current={current}
          settingsChanged={!!n && run.settingsHash !== n.settingsHash}
          attached={n ? totalAttachments(n.attachments) : 0}
          onPlanAgain={() => void start({ kind: 'plan' })}
        />
      ) : (
        <LiveRun
          run={run}
          status={status}
          log={log}
          deployment={undefined}
          boundPlan={undefined}
          versionDoc={undefined}
          deployedResources={undefined}
          onPlanAgain={() => void start({ kind: 'plan' })}
          labelOf={(id) => componentLabel(id, names)}
          network={n ? { id: n.id, name: n.name } : null}
          successAction={
            <Link className="btn btn-sm" to={`/networks/${networkId}`}>
              {r.openNetwork}
            </Link>
          }
        />
      )}
      {applying && n && (
        <ApplyNetworkDialog network={n} planRunId={run.id} onClose={() => setApplying(false)} />
      )}
    </PageContainer>
  );
}

/** A completed network plan (6.15.11): components instead of resources. */
function NetworkPlanReview({
  run,
  names,
  networkName,
  current,
  settingsChanged,
  attached,
  onPlanAgain,
}: {
  run: Run;
  names: ReadonlyMap<string, string>;
  networkName: string;
  current: boolean;
  settingsChanged: boolean;
  attached: number;
  onPlanAgain: () => void;
}) {
  const plan = run.result?.plan ?? { changes: [], sharedChanges: [] };
  const [chips, setChips] = useState<ReadonlySet<ChangeAction>>(new Set());
  const [hideUnchanged, setHideUnchanged] = useState(true);
  const counts = Object.fromEntries(
    ACTIONS.map((a) => [a, plan.changes.filter((c) => c.action === a).length]),
  ) as Record<ChangeAction, number>;
  const ordered = componentOrder(plan.changes);
  const rows = ordered.filter((c) =>
    chips.size === 0 ? !(hideUnchanged && c.action === 'no-change') : chips.has(c.action),
  );
  const outages = plan.changes.filter(isOutage);
  const newAddresses = plan.changes.filter(changesEndpoint);
  const impact = run.result?.impact ?? null;
  const diagnostics = run.result?.diagnostics ?? [];
  const planWarnings = diagnostics.filter((d) => d.severity !== 'error' && PLAN_WARNINGS.has(d.code));
  const toApply = impact?.deploymentsToApply ?? [];
  const staleReasons = [
    settingsChanged ? r.staleReasons.settingsChanged : null,
    !current && !settingsChanged ? r.staleReasons.appliedAfter : null,
  ].filter(Boolean);

  return (
    <div className="plan-review">
      <div className="plan-main">
        {run.status === 'completed' &&
          (current ? (
            <p className="callout callout-info" data-testid="plan-freshness">
              <ShieldCheck aria-hidden className="size-4 shrink-0" />
              <span>
                <span className="font-semibold">{r.current}</span>{' '}
                {r.currentDetail(shortHash(run.settingsHash), networkName)}
              </span>
            </p>
          ) : (
            <div className="callout callout-warning" data-testid="plan-freshness">
              <TriangleAlert aria-hidden className="size-4 shrink-0" />
              <span className="min-w-0 flex-1">
                <span className="font-semibold">{r.stale}</span> {staleReasons.join('; ')}.
              </span>
              <button type="button" className="btn btn-sm" onClick={onPlanAgain}>
                {p.planAgain}
              </button>
            </div>
          ))}

        {impact?.summary && (
          <p className="callout callout-info" data-testid="network-plan-impact">
            <Info aria-hidden className="size-4 shrink-0" />
            <span>
              <span className="font-semibold">{r.impact}:</span> {impact.summary}
            </span>
          </p>
        )}

        {outages.length > 0 && (
          <div role="alert" className="callout callout-danger" data-testid="plan-dangers">
            <TriangleAlert aria-hidden className="size-4 shrink-0" />
            <div>
              <p className="font-semibold">{p.dangerTitle}</p>
              <ul className="mt-1 list-disc pl-5">
                {outages.map((c) => (
                  <li key={c.resourceId}>{outageSentence(c, names)}</li>
                ))}
              </ul>
              <p className="mt-1">{r.typedNote(networkName)}</p>
            </div>
          </div>
        )}

        {newAddresses.length > 0 && (
          <div className="callout callout-warning" data-testid="plan-endpoint-changes">
            <TriangleAlert aria-hidden className="size-4 shrink-0" />
            <div>
              <p className="font-semibold">{r.endpointTitle}</p>
              <ul className="mt-1 list-disc pl-5">
                {newAddresses.map((c) => (
                  <li key={c.resourceId}>{r.endpointChanges(componentLabel(c.resourceId, names))}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {planWarnings.map((d, i) => (
          <div
            key={`${d.code}-${i}`}
            className="callout callout-warning"
            data-testid={`plan-warning-${d.code}`}
          >
            <TriangleAlert aria-hidden className="size-4 shrink-0" />
            <div className="min-w-0 flex-1">
              <p>
                {d.message} <code className="font-mono text-xs">{d.code}</code>
              </p>
              {d.code === 'DEPLOYMENT_APPLY_NEEDED' && toApply.length > 0 && (
                <p className="mt-1">
                  {r.deploymentsToApply}{' '}
                  {toApply.map((x, j) => (
                    <Fragment key={x.deploymentId}>
                      {j > 0 && ', '}
                      <Link className="link underline" to={`/deployments/${x.deploymentId}`}>
                        {x.name}
                      </Link>
                    </Fragment>
                  ))}
                </p>
              )}
            </div>
          </div>
        ))}

        <RunIssues
          run={{
            ...run,
            result: run.result
              ? { ...run.result, diagnostics: diagnostics.filter((d) => !PLAN_WARNINGS.has(d.code)) }
              : null,
          }}
          deployment={undefined}
          onPlanAgain={onPlanAgain}
        />

        <div className="flex flex-wrap items-center gap-2" role="group" aria-label={p.chips}>
          {ACTIONS.map((action) => (
            <button
              key={action}
              type="button"
              className={`plan-chip tone-${actionPresentation[action].tone}`}
              aria-pressed={chips.has(action)}
              onClick={() =>
                setChips((currentChips) => {
                  const next = new Set(currentChips);
                  if (next.has(action)) next.delete(action);
                  else next.add(action);
                  return next;
                })
              }
            >
              <span className="glyph" aria-hidden>
                {GLYPH[action]}
              </span>
              {p.chip[action](counts[action])}
            </button>
          ))}
          <span className="flex-1" />
          <label className="switch">
            <input
              type="checkbox"
              role="switch"
              checked={hideUnchanged}
              onChange={(e) => setHideUnchanged(e.target.checked)}
            />
            {p.hideUnchanged}
          </label>
        </div>

        <section aria-labelledby="network-plan-components">
          <h2 id="network-plan-components" className="sr-only">
            {r.components}
          </h2>
          {plan.changes.length === 0 ? (
            <p className="help">{r.noComponents}</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-line bg-surface">
              <table className="table" data-testid="network-plan-table">
                <thead>
                  <tr>
                    <th scope="col">{p.action}</th>
                    <th scope="col">{p.resource}</th>
                    <th scope="col">{p.type}</th>
                    <th scope="col">{p.summary}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((c) => (
                    <tr key={c.resourceId} className={isOutage(c) ? 'plan-row-danger' : undefined}>
                      <td>
                        <StatusBadge presentation={actionPresentation[c.action]} />
                      </td>
                      <td className="font-mono font-semibold">{componentLabel(c.resourceId, names)}</td>
                      <td>{componentTypeLabel(c.type)}</td>
                      <td className="text-fg-muted">
                        {childrenSummary(c.children ?? []).join(' · ') || strings.common.dash}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>

      <aside className="plan-side" aria-label={p.beforeYouApply}>
        <section className="side-card" aria-labelledby="network-before-apply">
          <h2 id="network-before-apply" className="side-card-title">
            {p.beforeYouApply}
          </h2>
          <ul className="side-checklist" data-testid="before-you-apply">
            <li>
              <Info aria-hidden className="size-4 text-fg-muted" />
              {attached > 0 ? r.appliesWait(attached) : r.noAttached}
            </li>
            <li>
              {outages.length === 0 ? (
                <>
                  <ShieldCheck aria-hidden className="size-4 text-success" />
                  {r.noOutage}
                </>
              ) : (
                <>
                  <TriangleAlert aria-hidden className="size-4 text-danger" />
                  {p.danger(outages.length)}
                </>
              )}
            </li>
          </ul>
        </section>
        <section className="side-card" aria-labelledby="network-plan-run">
          <h2 id="network-plan-run" className="side-card-title">
            {p.runPanel}
          </h2>
          <dl className="side-facts">
            <dt>{p.kind}</dt>
            <dd>
              {strings.status.runKind[run.kind].toLowerCase()} · {run.scope ?? 'full'}
            </dd>
            <dt>{p.triggeredBy}</dt>
            <dd>{displayActor(run.createdBy)}</dd>
            <dt>{p.settings}</dt>
            <dd>{p.hash(shortHash(run.settingsHash))}</dd>
          </dl>
        </section>
      </aside>
    </div>
  );
}
